import React, { useState } from 'react';
import './AgentsSection.css';

const AgentsSection: React.FC = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);

  const regionalPresence: Record<string, {
    title: string;
    description: string;
    cities: string[];
    latency: string;
    type?: string;
  }> = {
    'edge-connectivity': {
      title: 'Edge Connectivity',
      description: 'We have strategic edge locations across major Indian cities providing ultra-low latency connectivity and high availability for your telephony needs.',
      cities: ['Bengaluru', 'Chennai', 'Ahmedabad', 'Hyderabad', 'Delhi', 'Noida', 'Gurgaon', 'Lucknow', 'Kolkata'],
      latency: '<25ms',
      type: 'edge'
    },
    'North India': {
      title: 'North India',
      description: 'Strategic PoPs in Delhi, Chandigarh, Jaipur, Lucknow, and Dehradun. Serving the northern states with <30ms latency and high availability.',
      cities: ['Delhi', 'Chandigarh', 'Jaipur', 'Lucknow', 'Dehradun'],
      latency: '<30ms'
    },
    'South India': {
      title: 'South India',
      description: 'Regional hubs in Bengaluru, Chennai, Hyderabad, and Kochi. Optimized for high concurrency and low jitter across southern states.',
      cities: ['Bengaluru', 'Chennai', 'Hyderabad', 'Kochi', 'Thiruvananthapuram'],
      latency: '<25ms'
    },
    'East India': {
      title: 'East India',
      description: 'Coverage across Kolkata, Patna, Bhubaneswar, and Guwahati. Ensuring reliable connectivity for eastern business hubs.',
      cities: ['Kolkata', 'Patna', 'Bhubaneswar', 'Guwahati', 'Siliguri'],
      latency: '<35ms'
    },
    'West India': {
      title: 'West India',
      description: 'Major presence in Mumbai, Pune, Ahmedabad, and Surat. High-capacity infrastructure for western business centers.',
      cities: ['Mumbai', 'Pune', 'Ahmedabad', 'Surat', 'Nagpur'],
      latency: '<20ms'
    },
    'Central India': {
      title: 'Central India',
      description: 'Strategic locations in Bhopal, Indore, Raipur, and Jabalpur. Connecting central India with reliable telephony services.',
      cities: ['Bhopal', 'Indore', 'Raipur', 'Jabalpur', 'Gwalior'],
      latency: '<40ms'
    }
  };

  const openPopup = (region: string) => {
    setSelectedRegion(region);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setSelectedRegion(null);
  };

  return (
    <section id="agents" className="agents-section">
      <div className="agents-container">
        <div className="agents-header">
          <h2 className="agents-title">Connect agents anywhere in India.</h2>
          <p className="agents-description">
            Taar.AI Dialer integrates with your web dashboard, Android, or iOS app for zero lag for your teams. 
            Route calls through the nearest point of presence across all 28 states and 8 union territories — even the Andamans — 
            for unmatched low latency. Powered by secure private networks and Microsoft-backed infra, calls stay inside India 
            and fully DoT compliant. Track jitter, MoS, and packet loss in real-time. Scale from 5 to 50,000 agents without 
            rigid slabs or hidden commitments.
          </p>
          <button onClick={() => openPopup('edge-connectivity')} className="agents-cta">Read more about our edge connectivity</button>
        </div>
        
        <div className="agents-content">
          {/* First Image with Description */}
          <div className="agents-visual-item">
            <div className="agents-image-container">
              <img 
                src="https://assets.taar.ai/edge.png" 
                alt="Nationwide Edge Coverage" 
                className="agents-image"
              />
            </div>
            <div className="agents-text-content">
              <h3 className="agents-feature-title">Nationwide Deployment Hubs</h3>
              <p className="agents-feature-description">
                SIP PoPs across India — routed for &lt;50ms latency, full compliance, and auto-failover. 
                Strategic infrastructure placement across all major cities and regions ensures optimal call quality 
                and minimal latency for your customers.
              </p>
            </div>
          </div>

          {/* Second Image with Description */}
          <div className="agents-visual-item">
            <div className="agents-image-container">
              <img 
                src="https://assets.taar.ai/shortest.png" 
                alt="Low Latency Connections" 
                className="agents-image"
              />
            </div>
            <div className="agents-text-content">
              <h3 className="agents-feature-title">Low Latency Connections</h3>
              <p className="agents-feature-description">
                Route calls through the nearest point of presence for unmatched low latency. 
                Sub-50ms latency across the entire country with automatic failover and intelligent routing 
                for the best possible call experience.
              </p>
            </div>
          </div>
        </div>

        {/* Regional Presence Section */}
        <div className="regional-presence-section">
          <h3 className="presence-title">Regional Presence</h3>
          <p className="presence-subtitle">Click on any region to learn more about our infrastructure and coverage.</p>
          <div className="region-grid">
            {Object.keys(regionalPresence).map((region) => (
              <button
                key={region}
                className="region-button"
                onClick={() => openPopup(region)}
              >
                {region}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Regional Presence Popup */}
      {isPopupOpen && selectedRegion && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close" onClick={closePopup}>×</button>
            <div className="popup-header">
              <h3 className="popup-title">{regionalPresence[selectedRegion as keyof typeof regionalPresence].title}</h3>
              {regionalPresence[selectedRegion as keyof typeof regionalPresence].type !== 'edge' && (
                <div className="popup-latency">
                  <span className="latency-label">Typical Latency:</span>
                  <span className="latency-value">{regionalPresence[selectedRegion as keyof typeof regionalPresence].latency}</span>
                </div>
              )}
            </div>
            <p className="popup-description">
              {regionalPresence[selectedRegion as keyof typeof regionalPresence].description}
            </p>
            <div className="popup-cities">
              <h4>{regionalPresence[selectedRegion as keyof typeof regionalPresence].type === 'edge' ? 'Edge Locations:' : 'Key Cities:'}</h4>
              <div className="city-tags">
                {regionalPresence[selectedRegion as keyof typeof regionalPresence].cities.map((city) => (
                  <span key={city} className="city-tag">{city}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AgentsSection;
